
import { createRoot, hydrateRoot } from 'react-dom/client';
import { BrowserRouter } from "react-router-dom";
import App from './App.tsx';
import { HelmetProvider } from 'react-helmet-async';
import { getHydrationManager } from './utils/hydrationManager';
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ThemeProvider } from "@/components/theme-provider";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider } from "@/contexts/auth";
import { useStore } from "@/store/useStore";
import { debugLog, debugError } from "@/utils/debugLogger";
import { initOneSignalDeferred } from "@/lib/oneSignalDeferred";
import { bufferHydrationError } from "@/utils/hydrationAdminLogger";

// Importazione esplicita dei CSS
import './index.css';

const container = document.getElementById('root');

if (!container) {
  throw new Error('Failed to find the root element');
}

// Create a new QueryClient instance with simplified options
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60 * 1000, // 1 minute
      refetchOnWindowFocus: false,
      enabled: true, // Always enable queries after hydration
    },
  },
});

// Create Helmet context to use throughout the app
const helmetContext = {};

// Initialize hydration manager for proper SEO navigation handling
getHydrationManager();

// Check if container has content (SSR mode)
const containerHasContent = container.innerHTML.trim() !== '' && 
                           container.hasAttribute('data-hydrate');

// 🆕 SSR CONTEXT HYDRATION: Parse __SSR_CONTEXT__ to ensure client hydrates with same data as server
// This is critical for avoiding hydration mismatch #418/#423 on /compro-oro routes
const parseSSRContext = (): any | undefined => {
  if (typeof window === 'undefined') return undefined;
  
  try {
    const ssrContextScript = document.getElementById('__SSR_CONTEXT__');
    if (ssrContextScript && ssrContextScript.textContent) {
      const parsed = JSON.parse(ssrContextScript.textContent);
      // PAGE WEIGHT FIX: orari deduplicati (fallback se lo script inline non è girato)
      if (Array.isArray(parsed?.__oh)) {
        const t = parsed.__oh;
        delete parsed.__oh;
        (parsed.dealers || []).forEach((d: any) => {
          if (d && typeof d.__ohi === 'number') { const i = d.__ohi; delete d.__ohi; d.opening_hours = t[i]; }
        });
      }
      // PAGE WEIGHT FIX: cityData.dealers è serializzato una sola volta in `dealers`
      if (parsed?.cityData?.__dealersRef) {
        delete parsed.cityData.__dealersRef;
        parsed.cityData.dealers = parsed.dealers;
      }
      debugLog('ssr', 'Parsed __SSR_CONTEXT__ for hydration parity', { 
        hasDealers: !!parsed?.dealers,
        hasCities: !!parsed?.cities,
        hasMetadata: !!parsed?.dealersMetadata 
      }, 'entry-client');
      return parsed;
    }
  } catch (error) {
    debugError('ssr', 'Error parsing __SSR_CONTEXT__', error, 'entry-client');
  }
  
  return undefined;
};

// Parse SSR context before hydration to ensure first render matches server
const ssrContext = parseSSRContext();

// App JSX with all necessary providers - aligned exactly with entry-server-conditional.tsx
// NO StrictMode, NO AuthSyncComponent, NO DataPreloader here (they are client-only in AppInner)
// 🆕 Pass ssrContext to App for hydration parity
const AppWithProviders = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <HelmetProvider context={helmetContext}>
        <BrowserRouter>
          <ThemeProvider defaultTheme="light" defaultMetal="gold" ssrMode={containerHasContent}>
            <TooltipProvider>
              <AuthProvider>
                <App ssrContext={ssrContext} />
              </AuthProvider>
            </TooltipProvider>
          </ThemeProvider>
        </BrowserRouter>
      </HelmetProvider>
    </QueryClientProvider>
  );
};

// Add a type declaration for the server render time and data
declare global {
  interface Window {
    __SERVER_RENDER_TIME?: number;
    __INITIAL_DEALER_DATA__?: Record<string, any>;
    __PREDICTIONS_DATA?: {
      predictions: any[];
    };
    __SERVER_STORE?: {
      metalPrices?: any;
      dealerData?: Record<string, any>;
    };
  }
}

// Initialize dealer data from server if available
if (typeof window !== 'undefined' && window.__SERVER_STORE?.dealerData) {
  debugLog('dealer', "Initializing dealer data from server", undefined, 'entry-client');
  window.__INITIAL_DEALER_DATA__ = window.__SERVER_STORE.dealerData;
}

// Extract predictions data from script tag if available
if (typeof window !== 'undefined') {
  const predictionsDataScript = document.getElementById('__PREDICTIONS_DATA__');
  if (predictionsDataScript) {
    try {
      const predictionsData = JSON.parse(predictionsDataScript.textContent || '{}');
      window.__PREDICTIONS_DATA = predictionsData;
      debugLog('general', "Initialized predictions data from server", { count: predictionsData.predictions?.length || 0 }, 'entry-client');
    } catch (error) {
      debugError('general', "Error parsing predictions data", error, 'entry-client');
    }
  }
}

// 🆕 Parse __DEALER_DETAIL_DATA__ from JSON script tag into window BEFORE hydration
// This ensures metalPrices are available when DealerDetail mounts (prevents €0,00 flash)
// Additionally, it backfills window.__METAL_PRICES__ (spot-standard) and primes the Zustand store before hydrateRoot().
if (typeof window !== 'undefined') {
  try {
    const dealerDetailScript = document.getElementById('__DEALER_DETAIL_DATA__');
    if (dealerDetailScript?.textContent) {
      const parsed = JSON.parse(dealerDetailScript.textContent);
      (window as any).__DEALER_DETAIL_DATA__ = parsed;

      // If __METAL_PRICES__ is missing or dealer-specific, derive a spot-standard shape from dealer-detail payload
      const hasSpotStandard =
        !!(window as any).__METAL_PRICES__?.gold &&
        typeof (window as any).__METAL_PRICES__?.gold?.price === 'number';

      if (!hasSpotStandard && parsed?.metalPrices) {
        const mp = parsed.metalPrices;
        const goldSrc = mp.gold || {};
        const silverSrc = mp.silver || {};

        const normalizedSpot = {
          gold: {
            price: goldSrc.price ?? goldSrc.marketPrice ?? 0,
            change: goldSrc.change ?? 0,
            changePercentage: goldSrc.changePercentage ?? 0,
            timestamp: goldSrc.timestamp ?? ''
          },
          silver: {
            price: silverSrc.price ?? silverSrc.marketPrice ?? 0,
            change: silverSrc.change ?? 0,
            changePercentage: silverSrc.changePercentage ?? 0,
            timestamp: silverSrc.timestamp ?? ''
          }
        };

        (window as any).__METAL_PRICES__ = normalizedSpot;
      }

      // Prime store BEFORE hydration so the first client render matches SSR (no post-hydration reset to 0)
      try {
        useStore.getState().initializeFromSSR?.();
      } catch {
        // ignore
      }

      debugLog('dealer', "Parsed __DEALER_DETAIL_DATA__ from script tag", {
        hasDealer: !!parsed?.dealer,
        hasMetalPrices: !!parsed?.metalPrices,
        hasSpotStandardMetalPrices: typeof (window as any).__METAL_PRICES__?.gold?.price === 'number'
      }, 'entry-client');
    }
  } catch (e) {
    debugError('dealer', "Error parsing __DEALER_DETAIL_DATA__", e, 'entry-client');
  }
}

// Clean up SSR data scripts to prevent interference with other pages during SPA navigation
const cleanupSSRData = () => {
  // Remove SSR data scripts from DOM
  const scriptIds = [
    '__BLOG_DATA__',
    '__COMPRO_ORO_CITTA_DATA__',
    '__FAQ_DATA__',
    '__PREDICTIONS_DATA__',
    '__SSR_CONTEXT__',
    '__COMPRO_ORO_DATA__',
    '__DEALER_DATA__',
    '__DEALER_DETAIL_DATA__'
  ];
  scriptIds.forEach((id) => {
    const el = document.getElementById(id);
    if (el) el.remove();
  });

  // Clean up SSR globals to prevent stale data on SPA navigation
  const windowKeysToClear = [
    '__BLOG_METADATA__',
    '__BLOG_CATEGORY_METADATA__',
    '__BLOG_DATA__',
    '__BLOG_POST__',
    '__PREDICTIONS_DATA__',
    '__SERVER_DEALERS__',
    '__SERVER_CITIES__',
    '__SERVER_FORMATTED_CITY__',
    '__DEALER_STATS',
    '__METAL_PRICES__',
    '__COMPRO_ORO_CITTA_DATA__',
    '__CITIES_STATS__',
    '__AGGREGATED_STATS__',
    '__FAQ_DATA__',
    '__SSR_CONTEXT__',
    '__INITIAL_DEALER_DATA__',
    '__DEALER_DETAIL_DATA__',
    '__COMPRO_ORO_DATA__',
    '__COMPRO_ORO_DATA_DATA__'
  ];
  windowKeysToClear.forEach((key) => {
    if ((window as any)[key] !== undefined) {
      delete (window as any)[key];
    }
  });

  debugLog('ssr', 'SSR data cleanup completed', undefined, 'entry-client');
};

/**
 * 🛡️ Hydration completion is now signaled by an in-app sentinel (AppInner).
 * Here we only provide a conservative fallback to avoid hanging forever.
 */
const scheduleHydrationFallback = () => {
  const manager = getHydrationManager();
  
  // Safety net: if sentinel doesn't fire (edge cases), complete after 5s
  setTimeout(() => {
    if (!manager.isHydrationComplete()) {
      debugLog('general', "Hydration complete via fallback timeout (5s)", undefined, 'entry-client');
      manager.completeHydration();
    }
  }, 5000);
};

/**
 * 🛡️ HYDRATION ERROR INSTRUMENTATION
 * Logs recoverable hydration errors with componentStack for debugging #418/#422/#425
 */
const onRecoverableError = (error: unknown, errorInfo: { componentStack?: string }) => {
  const err = error instanceof Error ? error : new Error(String(error));
  const route = typeof window !== 'undefined' ? window.location.pathname : 'unknown';
  
  const entry = {
    route,
    message: err.message,
    componentStack: errorInfo.componentStack || null,
    timestamp: Date.now()
  };

  if (typeof window !== 'undefined' && (window as any).__HYDRATION_DEBUG__) {
    // Flag debug attivo: log immediato per tutti + esponi su window
    (window as any).__HYDRATION_ERRORS = (window as any).__HYDRATION_ERRORS || [];
    (window as any).__HYDRATION_ERRORS.push(entry);
    console.warn(
      `[HYDRATION][recoverable] route=${route}`,
      '\n  message:', err.message,
      '\n  componentStack:', errorInfo.componentStack || '(n/a)'
    );
  } else {
    // Flag non attivo: buffer privato nel modulo (non su window, nessun output)
    bufferHydrationError(entry);
  }
};

// For hydration, use createRoot for empty containers (CSR) and hydrateRoot for non-empty containers (SSR)
if (containerHasContent) {
  // If container is not empty, we're in SSR mode - hydrate
  debugLog('general', "Hydrating from server-rendered content", undefined, 'entry-client');
  debugLog('general', "Server render time", { time: window.__SERVER_RENDER_TIME || 'unknown' }, 'entry-client');
  
  // Perform hydration with onRecoverableError to capture componentStack
  hydrateRoot(container, <AppWithProviders />, { onRecoverableError });
  
  // 🛡️ Fallback only; real hydration completion is signaled in-app
  scheduleHydrationFallback();
  
  // Clean up SSR data after hydration to prevent interference during SPA navigation
  setTimeout(() => {
    cleanupSSRData();
    debugLog('general', "SSR data cleanup completed after hydration", undefined, 'entry-client');
  }, 2000);
} else {
  // If container is empty, we're in CSR mode - create a new root
  debugLog('general', "Creating new root (CSR mode)", undefined, 'entry-client');
  createRoot(container).render(<AppWithProviders />);
  
  // 🛡️ Fallback only; real hydration completion is signaled in-app
  scheduleHydrationFallback();
}

// Initialize OneSignal after hydration via deferred loading (LCP optimization)
// This removes ~150-200ms from the critical rendering path
initOneSignalDeferred();
